import { observable, configure, action } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';

configure({ enforceActions: 'always' });

/**
 * «Jobb smartere»: the module recommendations the nightly backend job derived
 * for the user's clinic (GET /api/modulerecommendations/). Read by the
 * frontpage card (components/dyrejournal/smartTipCard.js) and the inline hints
 * (components/dyrejournal/moduleHint.js).
 * Design: docs/superpowers/specs/2026-11-04-module-recommendations-design.md
 */
class ModuleRecommendationStore extends LocalModel {
    constructor() {
        super('moduleRecommendation', {
            namePlural: 'moduleRecommendations',
            namePluralReal: 'modulerecommendations',
            sort: '-count',
            limit: 100,
            api: {
                search: {
                    url: '/api/modulerecommendations/',
                    params: {},
                },
                load: {
                    url: '/api/modulerecommendations/',
                    params: {},
                },
                save: {
                    url: '/api/modulerecommendations/',
                    params: {},
                },
                delete: {
                    url: '/api/modulerecommendations/',
                    params: {},
                },
            },
        });
        // shown/clicked are logged once per module and type per session.
        this.loggedEvents = {};
    }

    @observable moduleRecommendation = {};

    @observable moduleRecommendations = [];

    @observable loaded = false;

    @observable loadedCustomer = 0;

    /**
     * Load once per clinic: a user who switches clinic (user.customer changes)
     * must not see the previous clinic's evidence or snooze its rows.
     */
    async ensureLoaded(customerId) {
        const id = parseInt(customerId, 10) || 0;
        if (this.loaded && this.loadedCustomer === id) {
            return this.moduleRecommendations;
        }
        if (this.loadedCustomer !== id) {
            this.loggedEvents = {};
        }
        this.updateKeyValue('loadedCustomer', id);
        return this.loadRecommendations();
    }

    @action
    reset() {
        this.loggedEvents = {};
        this.updateKeyValue('moduleRecommendations', []);
        this.updateKeyValue('loaded', false);
    }

    @action
    async loadRecommendations() {
        let list = [];
        try {
            const response = await util.fetchApi('/api/modulerecommendations/', { method: 'GET' }, {});
            if (response?.status === 200 && Array.isArray(response.data?.recommendations)) {
                list = response.data.recommendations;
            }
        } catch (err) {
            list = [];
        }
        this.updateKeyValue('moduleRecommendations', list);
        this.updateKeyValue('loaded', true);
        return list;
    }

    @action
    async snooze(module) {
        const response = await util.fetchApi(`/api/modulerecommendations/${module}/snooze`, { method: 'PATCH' }, {});
        this.updateKeyValue('moduleRecommendations', this.moduleRecommendations.filter(r => r.module !== module));
        return response;
    }

    async logEvent(module, type) {
        const key = `${module}:${type}`;
        if (this.loggedEvents[key]) {
            return;
        }
        this.loggedEvents[key] = true;
        try {
            await util.fetchApi(`/api/modulerecommendations/${module}/event`, { method: 'POST' }, { type });
        } catch (err) {
            // Best-effort counter; never disturb the UI.
        }
    }

    has(module) {
        return this.moduleRecommendations.find(r => r.module === module);
    }
}

export { ModuleRecommendationStore };

const moduleRecommendationStore = new ModuleRecommendationStore();
export default moduleRecommendationStore;
